import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET, img, video } from './localAssets.js';
import {
	formatVideoDuration,
	normalizeLocalVideoItem,
	revokeLocalVideoItem,
} from './localVideo.js';

import { getMediaItemUrl, getQiniuSignedUrl } from './qiniuMedia.js';

export const VIDEO_LIST_TYPE = 'video-list';
export const VIDEO_LIST_DEFAULT_HEIGHT = 100;
export const VIDEO_LIST_COL_COUNT = 2;
export const VIDEO_LIST_COL_GAP = 12;
export const VIDEO_LIST_H5_CONTENT_WIDTH = 345;
export const VIDEO_LIST_DEFAULT_CARD_RADIUS = 8;
export const VIDEO_LIST_DEFAULT_VIDEO_RADIUS = 8;
export const VIDEO_LIST_CARD_PADDING = 12;

const DEFAULT_TITLE_COLOR = '#333333';
const DEFAULT_DESC_COLOR = '#999999';

// 默认视频文件的字节数
const VIDEO_LIST_DEFAULT_SIZE = {
	1: Math.round(3.49 * 1024 * 1024),
	2: Math.round(2.77 * 1024 * 1024), 
	3: Math.round(2.65 * 1024 * 1024), 
	4: Math.round(2.52 * 1024 * 1024),
};
const VIDEO_LIST_DEFAULT_SIZE_LABEL = {
	1: '3.49 MB',
	2: '2.77 MB',
	3: '2.65 MB',
	4: '2.52 MB',
};

const ITEM_PRESETS = [
	{
		id: 'dv-31001',
		title: '逐浪向晨光',
		description: '治愈风景',
		duration: 14,
		videoUrl: '/gedit/video/video-list-1.mp4',
		coverUrl: '/gedit/images/video-list-1.png',
	},
	{
		id: 'dv-31002',
		title: '粉色落日港',
		description: '治愈风景',
		duration: 12,
		videoUrl: '/gedit/video/video-list-2.mp4',
		coverUrl: '/gedit/images/video-list-2.png',
	},
	{
		id: 'dv-31003',
		title: '蓝调归途',
		description: '治愈风景',
		duration: 12,
		videoUrl: '/gedit/video/video-list-3.mp4',
		coverUrl: '/gedit/images/video-list-3.png',
	},
	{
		id: 'dv-31004',
		title: '落日与归帆',
		description: '治愈风景',
		duration: 12,
		videoUrl: '/gedit/video/video-list-4.mp4',
		coverUrl: '/gedit/images/video-list-4.png',
	},
];

const LOCAL_COVERS = LOCAL_ASSET.videoList;

function createDefaultCover(preset = {}, index = 0) {
	const filename = preset?.coverFilename || 'cover.jpg';
	const coverUrl = preset?.coverUrl
		? img(preset.coverUrl.replace('/gedit/images/', ''))
		: LOCAL_COVERS[index % LOCAL_COVERS.length];
	return {
		id: `video-list-cover-${uuidv4()}`,
		filename,
		previewUrl: coverUrl,
		url: '',
		local: false,
	};
}

function createDefaultVideo(preset = {}, index = 0) {
	const duration = preset.duration ?? 14;
	const filename = preset?.filename || '';
	const videoUrl = preset?.videoUrl
		? video(preset.videoUrl.replace('/gedit/video/', ''))
		: '';
	const presetIndex = index + 1;
	return {
		// 优先使用 preset 显式声明的 id（如 dv-20001），否则回退到 uuid（用户上传时）
		id: preset.id || `video-list-video-${uuidv4()}`,
		filename,
		// 优先读 preset 中的 filesize（外部预设可能有独立大小），否则用默认映射
		filesize: preset.filesize || VIDEO_LIST_DEFAULT_SIZE_LABEL[presetIndex] || '',
		filesize_byte: preset.filesize_byte || VIDEO_LIST_DEFAULT_SIZE[presetIndex] || 0,
		previewUrl: videoUrl,
		duration,
		durationText: formatVideoDuration(duration),
		url: videoUrl,
		local: false,
	};
}

export function createDefaultVideoListItem(preset = {}, index = 0) {
	return {
		id: preset.id || uuidv4(),
		title: preset.title || '王牌飞行员沙溢空战入侵',
		description: preset.description || '恰饭看电影',
		titleColor: DEFAULT_TITLE_COLOR,
		descColor: DEFAULT_DESC_COLOR,
		cover: createDefaultCover(preset, index),
		video: createDefaultVideo(preset, index),
	};
}

export function createDefaultVideoListItems(count = 4) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		items.push(createDefaultVideoListItem(ITEM_PRESETS[i] || {}, i));
	}
	return items;
}

export function getVideoListDefaults() {
	return normalizeVideoListData({
		items: createDefaultVideoListItems(4),
		showCard: false,
	});
}

export function isVideoListBlock(blockType) {
	return blockType === VIDEO_LIST_TYPE;
}

export function getVideoListCoverUrl(item) {
	if (!item?.cover) return '';
	const cover = item.cover;
	if (cover.previewUrl || cover.url) {
		return getMediaItemUrl(cover);
	}
	if (item?.video) {
		const rawUrl = item.video.url || item.video.previewUrl || '';
		if (rawUrl) {
			return getQiniuSignedUrl(rawUrl, 0);
		}
	}
	return '';
}

export function getVideoListVideoUrl(item) {
	if (!item?.video) return '';
	return getMediaItemUrl(item.video);
}

function normalizeCoverImage(image, fallback) {
	if (!image) {
		return { id: '', filename: '', previewUrl: '', url: '', local: false };
	}
	if (!image.previewUrl && !image.url) {
		return { id: '', filename: '', previewUrl: '', url: '', local: false };
	}
	return normalizeStoredImageItem(image, fallback);
}

function normalizeVideoAsset(video, fallback) {
	if (!video?.previewUrl && !video?.url && !video?.duration) {
		return fallback ? { ...fallback } : createDefaultVideo();
	}
	return normalizeLocalVideoItem({
		...fallback,
		...video,
	});
}

export function normalizeVideoListItem(item = {}, index = 0) {
	const fallback = createDefaultVideoListItem(ITEM_PRESETS[index] || {}, index);
	return {
		id: item.id || uuidv4(),
		title: item.title ?? fallback.title,
		description: item.description ?? fallback.description,
		titleColor: normalizeHexColor(item.titleColor || fallback.titleColor),
		descColor: normalizeHexColor(item.descColor || fallback.descColor),
		cover: normalizeCoverImage(item.cover, fallback.cover),
		video: normalizeVideoAsset(item.video, fallback.video),
	};
}

export function normalizeVideoListItems(items = []) {
	if (!items.length) {
		return createDefaultVideoListItems(4);
	}
	return items.map((item, index) => normalizeVideoListItem(item, index));
}

export function normalizeVideoListData(data = {}) {
	const height = Number(data.imageHeight);
	return {
		items: normalizeVideoListItems(data.items),
		showCard: data.showCard === true,
		cardBorderRadius: Number.isFinite(Number(data.cardBorderRadius))
			? Math.max(0, Number(data.cardBorderRadius))
			: VIDEO_LIST_DEFAULT_CARD_RADIUS,
		videoBorderRadius: Number.isFinite(Number(data.videoBorderRadius))
			? Math.max(0, Number(data.videoBorderRadius))
			: VIDEO_LIST_DEFAULT_VIDEO_RADIUS,
		imageHeight: Number.isFinite(height) && height > 0
			? height
			: VIDEO_LIST_DEFAULT_HEIGHT,
	};
}

export function getVideoListRefColumnWidth(contentWidth = VIDEO_LIST_H5_CONTENT_WIDTH) {
	const gaps = VIDEO_LIST_COL_GAP * (VIDEO_LIST_COL_COUNT - 1);
	return (contentWidth - gaps) / VIDEO_LIST_COL_COUNT;
}

export function getVideoListThumbAspectRatio(imageHeight = VIDEO_LIST_DEFAULT_HEIGHT) {
	const colWidth = getVideoListRefColumnWidth();
	const height = Number(imageHeight) || VIDEO_LIST_DEFAULT_HEIGHT;
	return `${colWidth} / ${height}`;
}

export function getVideoListBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizeVideoListData(data);
	const pxRatio = h5 ? 50 : 1;
	const videoRadius = normalized.videoBorderRadius;
	const cardRadius = normalized.cardBorderRadius;

	return {
		thumbAspectRatio: getVideoListThumbAspectRatio(normalized.imageHeight),
		videoBorderRadius: h5 ? `${videoRadius / pxRatio}rem` : `${videoRadius}px`,
		cardBorderRadius: h5 ? `${cardRadius / pxRatio}rem` : `${cardRadius}px`,
		cardPadding: h5 ? `${VIDEO_LIST_CARD_PADDING / pxRatio}rem` : `${VIDEO_LIST_CARD_PADDING}px`,
		showCard: normalized.showCard,
	};
}

export function replaceVideoListCover(currentCover, file, createLocalImageItem) {
	if (currentCover?.local) {
		revokeLocalImageUrl(currentCover.previewUrl);
	}
	return createLocalImageItem(file);
}

export function replaceVideoListVideo(currentVideo, nextVideo) {
	if (currentVideo?.local) {
		revokeLocalVideoItem(currentVideo);
	}
	return normalizeLocalVideoItem(nextVideo);
}

export function revokeVideoListItems(items = []) {
	items.forEach((item) => {
		if (item?.cover?.local) {
			revokeLocalImageUrl(item.cover.previewUrl);
		}
		if (item?.video?.local) {
			revokeLocalVideoItem(item.video);
		}
	});
}
